import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Globe, Mail, MapPin, User } from 'lucide-react';

export const webEmbedAssistant: Playbook = {
  meta: {
    id: 'web-embed-assistant',
    slug: 'web-embed-assistant',
    categoryId: 'provide-support',
    name: 'The Web Embed Genie',
    tagline: 'Embedded help widget on every page.',
    description: 'Add a persistent help widget to your website that follows users across pages, providing contextual assistance and capturing leads throughout their journey.',
    strategy: 'Add a persistent help widget to your website that follows users across pages, providing contextual assistance and capturing leads throughout their journey.',
    benefits: [
      'Contextual help on every page',
      'Seamless user experience',
      'Lead capture opportunities',
      'Reduced bounce rate'
    ],
    targetAudience: ['SaaS companies', 'E-commerce websites', 'Documentation sites'],
    estimatedSetupTime: '10 minutes',
    difficulty: 'beginner',
    tags: ['embedded', 'website', 'contextual', 'persistent'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Web Embed Genie Playbook - Persistent Help Widget',
    metaDescription: 'Deploy a persistent Genie widget across your website for contextual help and lead capture on every page.',
    keywords: ['web embed Genie', 'persistent widget', 'contextual help', 'website Genie', 'Genie widget']
  },
  demoConfig: {
    triggerType: 'button',
    message: "Hi! I'm always here in the corner if you need help. I can see you're on the docs page - need help finding something specific?",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! I'm your Genie. I'm here on every page if you need help." },
    { speaker: 'user', message: "How do I integrate your API?" },
    { speaker: 'agent', message: "I can see you're looking at our docs. Let me guide you to the API section." },
    { speaker: 'user', message: "That would be great" },
    { speaker: 'agent', message: "Here's the quickstart guide, and I can walk you through each step!" }
  ],
  templateConfig: {
    firstMessage: "Hi! I'm your website Genie. I'm here to help you navigate, answer questions, or connect you with our team.",
    systemPrompt: "You are an embedded website Genie. You're available on every page to provide contextual help, answer questions, guide navigation, and capture leads. Be aware of which page the user is on and provide relevant assistance.",
    tools: ['page_context', 'navigation_guide', 'knowledge_base_search', 'email_capture', 'calendar_booking']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'User visits any page',
      description: 'Visitor navigates to any page on your website and sees the persistent Genie'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Provides contextual awareness',
      description: 'Genie understands current page context and user journey to offer relevant help'
    },
    {
      type: 'action',
      icon: MapPin,
      genieIcon: 'share',
      label: 'Guides navigation',
      description: 'Helps users find specific sections, features, or information based on their needs'
    },
    {
      type: 'action',
      icon: Globe,
      genieIcon: 'settings',
      label: 'Answers page-specific questions',
      description: 'Provides relevant information about current page content, features, or functionality'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Captures leads across journey',
      description: 'Identifies opportunities to collect contact information throughout user journey'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Seamless support experience',
      description: 'Users get consistent help across entire website, improving engagement and conversions'
    }
  ],
  metrics: {
    conversionRate: '15%',
    responseTime: '<2 seconds',
    satisfactionScore: '4.6/5',
    costSavings: '$25k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'DevTools Inc',
      authorName: 'Mark Rodriguez',
      authorTitle: 'Product Manager',
      quote: 'The embedded Genie reduced our documentation support requests by 45%. Users love the contextual help.',
      metrics: '45% reduction in docs support'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Web Embed Setup Guide',
      description: 'Complete guide to embedding your Genie across your website',
      url: '/docs/web-embed-setup'
    },
    {
      type: 'template',
      title: 'Contextual Response Templates',
      description: 'Pre-built responses for different page types and contexts',
      url: '/templates/contextual-responses'
    }
  ]
};